import { type OperatorSlug, operatorBrand, type UnconfirmedReason } from '@ev/shared'
import type { RefObject } from 'react'
import { useStrings } from '../i18n'
import { type ColorScheme, MAP_THEMES, type PowerBand } from '../theme/palette'

/**
 * The hover card.
 *
 * The element is always mounted and moved by writing `transform` on the ref
 * from the pointer handler, so following the cursor never costs a React
 * render; only changing which site is under the cursor does.
 */

export interface HoveredSite {
  readonly id: string
  readonly name: string
  readonly city: string
  readonly operator: OperatorSlug
  readonly ac: number
  readonly dc: number
  readonly pmax: number
  readonly band: PowerBand
  /** Why the pin carries the warning mark, or `null` when it does not. */
  readonly unconfirmed: UnconfirmedReason | null
}

export const MapTooltip = ({
  site,
  scheme,
  elementRef,
}: {
  site: HoveredSite | null
  scheme: ColorScheme
  elementRef: RefObject<HTMLDivElement | null>
}) => {
  const strings = useStrings()
  const theme = MAP_THEMES[scheme]

  return (
    <div
      ref={elementRef}
      className="map__tooltip"
      data-visible={site !== null}
      aria-hidden="true"
      role="presentation"
    >
      {site !== null && (
        <>
          <p className="map__tooltip-name">{site.name}</p>
          <p className="map__tooltip-meta">
            {site.city} · {operatorBrand(site.operator).displayName}
          </p>
          <p className="map__tooltip-power">
            <span
              className="map__tooltip-swatch"
              style={{ background: theme.band[site.band] }}
              aria-hidden="true"
            />
            <strong>
              {site.pmax} {strings.filters.minPowerUnit}
            </strong>
            <span className="map__tooltip-points">{strings.map.points(site.ac, site.dc)}</span>
          </p>
          {site.unconfirmed !== null && (
            // The mark on the pin is a glyph and says nothing on its own; this is
            // where it acquires a meaning without the reader having to click. Which
            // meaning, not merely that there is one - the two findings differ in
            // whether the charger might be missing at all, and the reason rides on
            // the feature so the card can say which without fetching anything.
            <p className="map__tooltip-unconfirmed">
              {strings.unconfirmed.badge[site.unconfirmed]}
            </p>
          )}
        </>
      )}
    </div>
  )
}
